<template>
	<v-dialog
		v-model="dialog"
		max-width="800"
		persistent
		scrollable
	>
		<v-card>
			<v-card-title class="d-flex justify-space-between align-center">
				<span style="font-size: 24px; font-weight: 700; color: #141414;">
					Configure seu Gabinete
				</span>
				<v-btn
					icon
					@click="closeDialog"
					:disabled="loading"
				>
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text>
				<p style="color: #4d4d4d; margin-bottom: 24px; font-size: 15px;">
					Indique até 9 integrantes do seu gabinete. Eles terão acesso ao plano Legislativo sem necessidade de pagamento.
				</p>

				<!-- Lista de integrantes adicionados -->
				<div
					v-if="integrantes.length > 0"
					class="mb-4"
				>
					<h4 style="font-size: 16px; font-weight: 600; margin-bottom: 12px; color: #141414;">
						Integrantes adicionados ({{ integrantes.length }}/9)
					</h4>
					<v-chip
						v-for="(integrante, index) in integrantes"
						:key="`${integrante.userId || integrante.codigoCupom || index}-${integrante.tipo}`"
						class="ma-1"
						color="#F15A24"
						text-color="white"
						close
						@click:close="removerIntegrante(index)"
					>
						<v-avatar
							left
							size="24"
						>
							<v-icon size="16">mdi-account</v-icon>
						</v-avatar>
						{{ integrante.nome || integrante.email || 'Convite pendente' }}
					</v-chip>
				</div>

				<!-- Campo de busca -->
				<div class="d-flex align-center mb-3">
					<v-text-field
						v-model="nomeBusca"
						label="Buscar por nome ou e-mail"
						placeholder="Digite o nome ou e-mail do integrante"
						prepend-inner-icon="mdi-magnify"
						color="#F15A24"
						filled
						hide-details
						clearable
						class="mr-2"
						@keyup.enter="buscarUsuario"
					/>
					<v-btn
						color="#F15A24"
						dark
						:loading="buscandoUsuario"
						:disabled="!nomeBusca || nomeBusca.trim().length < 2"
						@click="buscarUsuario"
					>
						<v-icon>mdi-magnify</v-icon>
					</v-btn>
				</div>

				<!-- Lista de usuários encontrados -->
				<v-list
					v-if="usuariosEncontrados.length > 0"
					class="mb-3"
					style="max-height: 300px; overflow-y: auto;"
				>
					<v-list-item
						v-for="user in usuariosEncontrados"
						:key="user.id"
						class="chip-with-hover"
						style="padding: 0.5rem; margin-bottom: 0.5rem; border: 1px solid #e0e0e0; border-radius: 8px;"
					>
						<v-list-item-icon class="mr-3">
							<v-icon
								v-if="!user.avatar"
								size="40px"
								color="#F15A24"
							>
								mdi-account
							</v-icon>
							<v-img
								v-if="user.avatar"
								width="40px"
								height="40px"
								class="rounded-circle"
								:src="`https://api.mylegis.com.br/assets/${user.avatar}`"
							/>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title>
								{{ user.first_name }} {{ user.last_name || '' }}
							</v-list-item-title>
							<v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<v-btn
								color="#F15A24"
								dark
								small
								@click="adicionarUsuarioExistente(user)"
								:disabled="usuarioJaAdicionado(user) || integrantes.length >= 9"
							>
								<v-icon left small>mdi-plus</v-icon>
								Adicionar
							</v-btn>
						</v-list-item-action>
					</v-list-item>
				</v-list>

				<!-- Mensagem quando não encontrou usuários -->
				<v-alert
					v-if="buscou && usuariosEncontrados.length === 0"
					type="info"
					dense
					text
					class="mb-3"
				>
					Nenhum usuário com plano legislativo encontrado. 
					<span
						style="color: #F15A24; cursor: pointer; font-weight: 600; text-decoration: underline;"
						@click="mostrarFormularioConvite = true"
					>
						Gerar link de convite
					</span>
				</v-alert>

				<!-- Formulário de geração de link de convite -->
				<v-expand-transition>
					<v-card
						v-if="mostrarFormularioConvite"
						class="mt-4"
						elevation="2"
						style="background-color: #f5f5f5;"
					>
						<v-card-text>
							<div class="d-flex justify-space-between align-center mb-3">
								<h4 style="font-size: 16px; font-weight: 600; color: #141414;">
									Gerar link de convite
								</h4>
								<v-btn
									icon
									small
									@click="mostrarFormularioConvite = false"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</div>
							<p style="color: #4d4d4d; font-size: 14px; margin-bottom: 16px;">
								Clique no botão abaixo para gerar um link de convite. Compartilhe este link com o integrante do gabinete.
							</p>
							
							<!-- Exibir link gerado acima do botão -->
							<v-expand-transition>
								<div v-if="linkConviteGerado" class="mb-4">
									<v-text-field
										:value="linkConviteGerado"
										label="Link de convite"
										readonly
										prepend-inner-icon="mdi-link"
										color="#F15A24"
										filled
										hide-details
										class="mb-2"
									>
										<template v-slot:append>
											<v-btn
												icon
												small
												@click="copiarLink"
												:disabled="copiandoLink"
												:color="linkCopiado ? 'success' : ''"
											>
												<v-icon>{{ linkCopiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
											</v-btn>
										</template>
									</v-text-field>
									<v-alert
										v-if="linkCopiado"
										type="success"
										dense
										text
										class="mb-0"
									>
										Link copiado para a área de transferência!
									</v-alert>
								</div>
							</v-expand-transition>
							
							<v-btn
								color="#F15A24"
								dark
								block
								@click="gerarLinkConvite"
								:loading="gerandoLink"
								:disabled="integrantes.length >= 9"
							>
								<v-icon left>mdi-link-variant</v-icon>
								{{ linkConviteGerado ? 'Gerar Novo Link' : 'Gerar Link de Convite' }}
							</v-btn>
						</v-card-text>
					</v-card>
				</v-expand-transition>
			</v-card-text>

			<v-card-actions class="d-flex justify-space-between">
				<v-btn
					text
					@click="closeDialog"
					:disabled="loading"
				>
					Cancelar
				</v-btn>
				<v-btn
					color="#F15A24"
					dark
					@click="salvarIntegrantes"
					:loading="loading"
					:disabled="loading"
				>
					Salvar ({{ integrantes.length }}/9)
				</v-btn>
			</v-card-actions>
		</v-card>

		<!-- Snackbar para mensagens -->
		<v-snackbar
			v-model="snackbar"
			:color="snackbarColor"
			timeout="3000"
			top
		>
			{{ snackbarText }}
			<template v-slot:action="{ attrs }">
				<v-btn
					text
					v-bind="attrs"
					@click="snackbar = false"
				>
					Fechar
				</v-btn>
			</template>
		</v-snackbar>
	</v-dialog>
</template>

<script>
export default {
	name: 'DialogIntegrantesGabinete',
	props: {
		value: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			dialog: this.value,
			nomeBusca: '',
			usuariosEncontrados: [],
			buscandoUsuario: false,
			buscou: false,
			integrantes: [],
			mostrarFormularioConvite: false,
			gerandoLink: false,
			linkConviteGerado: '',
			copiandoLink: false,
			linkCopiado: false,
			loading: false,
			snackbar: false,
			snackbarText: '',
			snackbarColor: 'success',
			grupoGabineteId: null,
		};
	},
	computed: {
		usuarios() {
			return this.$store.state.users;
		},
	},
	watch: {
		value(newVal) {
			this.dialog = newVal;
			if (newVal) {
				this.carregarIntegrantesExistentes();
			} else {
				// Limpar campos quando fechar o dialog
				this.nomeBusca = '';
				this.usuariosEncontrados = [];
				this.buscou = false;
				this.mostrarFormularioConvite = false;
			}
		},
		dialog(newVal) {
			this.$emit('input', newVal);
		},
	},
	methods: {
		usuarioJaAdicionado(user) {
			if (!user || !user.id) return false;
			// Verificar se o usuário já está na lista (como integrante ou como convite pendente)
			return this.integrantes.some(i => i.userId === user.id);
		},
		async carregarIntegrantesExistentes() {
			try {
				// Buscar grupo exclusivo do gabinete do usuário
				// O grupo deve ter o user_created igual ao usuário atual, ser restrito e ter nome começando com "Gabinete"
				const { data } = await this.$http.get(
					`items/grupos?filter[user_created][_eq]=${this.$store.state.user.id}&filter[restrito][_eq]=true&filter[nome_grupo][_contains]=Gabinete&fields=*,integrantes.directus_users_id.id,integrantes.directus_users_id.email,integrantes.directus_users_id.first_name`
				);
				
				if (data.data && data.data.length > 0) {
					const grupoGabinete = data.data[0];
					this.grupoGabineteId = grupoGabinete.id;
					
					// Mapear integrantes do grupo (excluindo o próprio admin)
					if (grupoGabinete.integrantes && grupoGabinete.integrantes.length > 0) {
						// Buscar informações completas de cada integrante para verificar se foi convidado por link
						const integrantesComInfo = await Promise.all(
							grupoGabinete.integrantes
								.filter(item => item.directus_users_id && item.directus_users_id.id !== this.$store.state.user.id)
								.map(async (item) => {
									try {
										const { data: userData } = await this.$http.get(
											`users/${item.directus_users_id.id}?fields=id,eh_convidado_gabinete,admin_gabinete_id,email,first_name`
										);
										return {
											...item,
											eh_convidado_gabinete: userData.data.eh_convidado_gabinete || false,
											admin_gabinete_id: userData.data.admin_gabinete_id || null,
											email: userData.data.email || item.directus_users_id.email,
											first_name: userData.data.first_name || item.directus_users_id.first_name,
										};
									} catch (err) {
										window.console.warn(`Erro ao buscar info do usuário ${item.directus_users_id.id}:`, err);
										return {
											...item,
											eh_convidado_gabinete: false,
											admin_gabinete_id: null,
											email: item.directus_users_id.email,
											first_name: item.directus_users_id.first_name,
										};
									}
								})
						);
						
						// Mapear para o formato da lista local
						const integrantesReais = integrantesComInfo.map(function(item) {
							return {
								userId: item.directus_users_id.id,
								email: item.email || '',
								nome: item.first_name || 'Usuário',
								tipo: 'usuario',
								integranteId: item.id, // ID do relacionamento para remoção
								eh_convidado_gabinete: item.eh_convidado_gabinete, // Flag para identificar convidados por link
								admin_gabinete_id: item.admin_gabinete_id, // ID do admin que convidou
							};
						});
						
						// Obter IDs dos integrantes reais para verificar se algum convite já foi convertido
						const integrantesIds = integrantesReais.map(i => i.userId).filter(id => id);
						
						// Carregar convites pendentes da tabela add_grupos para este grupo
						let convitesPendentesAddGrupos = [];
						try {
							const { data: convitesData } = await this.$http.get(
								`items/add_grupos?filter[id_grupo][_eq]=${this.grupoGabineteId}&filter[status][_eq]=pendente&fields=id,id_usuario,id_usuario.id,id_usuario.first_name,id_usuario.email`
							);
							
							convitesPendentesAddGrupos = (convitesData.data || []).map(convite => ({
								userId: convite.id_usuario ? convite.id_usuario.id : null,
								email: convite.id_usuario ? convite.id_usuario.email || '' : '',
								nome: convite.id_usuario ? convite.id_usuario.first_name || 'Usuário' : 'Usuário',
								tipo: 'convite_pendente',
								conviteId: convite.id,
							}));
							
							window.console.log('✅ Convites pendentes carregados da tabela add_grupos:', convitesPendentesAddGrupos.length);
						} catch (err) {
							window.console.warn('Erro ao carregar convites pendentes:', err);
						}
						
						// Manter convites por link que ainda não foram convertidos
						// (convites têm userId: null e tipo: 'convite')
						const convitesLink = this.integrantes.filter(i => 
							i.tipo === 'convite' && 
							!i.userId && // Convites pendentes não têm userId ainda
							!integrantesIds.includes(i.userId) // E não correspondem a nenhum usuário já cadastrado
						);
						
						// Combinar: integrantes reais + convites pendentes da tabela add_grupos + convites por link
						this.integrantes = [
							...integrantesReais,
							...convitesPendentesAddGrupos,
							...convitesLink
						];
						
						window.console.log('✅ Integrantes carregados:', this.integrantes.length);
						window.console.log('✅ Integrantes convidados por link:', integrantesReais.filter(i => i.eh_convidado_gabinete).length);
					} else {
						// Se não há integrantes reais, carregar apenas convites pendentes da tabela add_grupos
						let convitesPendentesAddGrupos = [];
						try {
							const { data: convitesData } = await this.$http.get(
								`items/add_grupos?filter[id_grupo][_eq]=${this.grupoGabineteId}&filter[status][_eq]=pendente&fields=id,id_usuario,id_usuario.id,id_usuario.first_name,id_usuario.email`
							);
							
							convitesPendentesAddGrupos = (convitesData.data || []).map(convite => ({
								userId: convite.id_usuario ? convite.id_usuario.id : null,
								email: convite.id_usuario ? convite.id_usuario.email || '' : '',
								nome: convite.id_usuario ? convite.id_usuario.first_name || 'Usuário' : 'Usuário',
								tipo: 'convite_pendente',
								conviteId: convite.id,
							}));
						} catch (err) {
							window.console.warn('Erro ao carregar convites pendentes:', err);
						}
						
						const convitesLink = this.integrantes.filter(i => i.tipo === 'convite');
						this.integrantes = [...convitesPendentesAddGrupos, ...convitesLink];
					}
				} else {
					// Se não há grupo, manter apenas convites por link
					this.integrantes = this.integrantes.filter(i => i.tipo === 'convite');
				}
			} catch (error) {
				window.console.error('Erro ao carregar integrantes:', error);
				// Em caso de erro, manter apenas convites pendentes
				this.integrantes = this.integrantes.filter(i => i.tipo === 'convite');
			}
		},
		async buscarUsuario() {
			if (!this.nomeBusca || this.nomeBusca.trim().length < 2) {
				this.usuariosEncontrados = [];
				this.buscou = false;
				return;
			}
			
			this.buscandoUsuario = true;
			this.buscou = false;
			
			try {
				const searchTerm = this.nomeBusca.trim();
				
				// Buscar por nome (usando o store) e também por email (busca direta)
				// Primeiro, buscar por nome usando o store
				await this.$store.dispatch('searchUsuarios', searchTerm);
				let usuariosPorNome = this.usuarios.filter(user => 
					user.id !== this.$store.state.user.id
				);
				
				// Também buscar por email diretamente
				let usuariosPorEmail = [];
				try {
					const { data } = await this.$http.get(
						`users?filter[email][_contains]=${searchTerm}&fields=id,first_name,last_name,email,avatar&limit=10`
					);
					usuariosPorEmail = (data.data || []).filter(user => 
						user.id !== this.$store.state.user.id
					);
				} catch (err) {
					window.console.warn('Erro ao buscar por email:', err);
				}
				
				// Combinar resultados e remover duplicatas
				const todosUsuarios = [...usuariosPorNome, ...usuariosPorEmail];
				const usuariosUnicos = todosUsuarios.filter((user, index, self) =>
					index === self.findIndex(u => u.id === user.id)
				);
				
				// IMPORTANTE: Filtrar apenas usuários com plano legislativo
				// Buscar informações de plano de todos os usuários
				let usuariosComPlano = [];
				
				try {
					const usuariosComInfo = await Promise.all(
						usuariosUnicos.map(async (user) => {
							try {
								const { data: userData } = await this.$http.get(
									`users/${user.id}?fields=id,plano`
								);
								return {
									...user,
									plano: userData.data.plano,
									temPlanoLegislativo: this.temPlanoLegislativo(userData.data.plano),
								};
							} catch (err) {
								return {
									...user,
									plano: null,
									temPlanoLegislativo: false,
								};
							}
						})
					);
					
					// Filtrar apenas usuários com plano legislativo
					usuariosComPlano = usuariosComInfo.filter(user => user.temPlanoLegislativo);
				} catch (err) {
					window.console.error('Erro ao verificar planos dos usuários:', err);
					usuariosComPlano = [];
				}
				
				this.usuariosEncontrados = usuariosComPlano;
				this.buscou = true;
			} catch (error) {
				window.console.error('Erro ao buscar usuário:', error);
				this.usuariosEncontrados = [];
				this.buscou = true;
				this.mostrarSnackbar('Erro ao buscar usuários. Tente novamente.', 'error');
			} finally {
				this.buscandoUsuario = false;
			}
		},
		temPlanoLegislativo(plano) {
			// Verificar se o plano é legislativo
			return plano === 'plano-legislativo' || plano === 'legislativo';
		},
		async adicionarUsuarioExistente(user) {
			window.console.log('🔵 adicionarUsuarioExistente chamado com:', user);
			
			if (!user) {
				window.console.warn('⚠️ Usuário não fornecido');
				return;
			}
			
			if (this.usuarioJaAdicionado(user)) {
				window.console.warn('⚠️ Usuário já adicionado');
				this.mostrarSnackbar('Este usuário já foi adicionado', 'warning');
				return;
			}
			
			if (this.integrantes.length >= 9) {
				this.mostrarSnackbar('Você pode adicionar no máximo 9 integrantes', 'warning');
				return;
			}

			// Verificar se não é o próprio usuário
			if (user.id === this.$store.state.user.id) {
				this.mostrarSnackbar('Você não pode adicionar a si mesmo', 'warning');
				return;
			}

			// Verificar se o usuário tem plano legislativo
			if (!user.temPlanoLegislativo) {
				this.mostrarSnackbar('Este usuário não possui plano legislativo', 'warning');
				return;
			}

			// Garantir que o grupo do gabinete existe
			if (!this.grupoGabineteId) {
				try {
					const { data } = await this.$http.get(
						`items/grupos?filter[user_created][_eq]=${this.$store.state.user.id}&filter[restrito][_eq]=true&filter[nome_grupo][_contains]=Gabinete&fields=id`
					);
					
					if (data.data && data.data.length > 0) {
						this.grupoGabineteId = data.data[0].id;
					} else {
						// Criar grupo do gabinete
						const { data: newGroupData } = await this.$http.post('items/grupos', {
							nome_grupo: `Gabinete de ${this.$store.state.user.first_name}`,
							restrito: true,
							tipo_grupo: 'gabinete',
							status: 'published',
							user_created: this.$store.state.user.id,
							administradores: [{
								directus_users_id: this.$store.state.user.id
							}],
							integrantes: [{
								directus_users_id: this.$store.state.user.id
							}]
						});
						this.grupoGabineteId = newGroupData.data.id;
					}
				} catch (err) {
					window.console.error('Erro ao buscar/criar grupo do gabinete:', err);
					this.mostrarSnackbar('Erro ao preparar convite. Tente novamente.', 'error');
					return;
				}
			}

			// Verificar se já existe um convite pendente para este usuário neste grupo
			try {
				const { data: convitesExistentes } = await this.$http.get(
					`items/add_grupos?filter[id_grupo][_eq]=${this.grupoGabineteId}&filter[id_usuario][_eq]=${user.id}&filter[status][_eq]=pendente&fields=id`
				);
				
				if (convitesExistentes.data && convitesExistentes.data.length > 0) {
					this.mostrarSnackbar('Já existe um convite pendente para este usuário', 'warning');
					return;
				}
			} catch (err) {
				window.console.warn('Erro ao verificar convites existentes:', err);
			}

			// Criar convite na tabela add_grupos
			try {
				const convite = {
					status: 'pendente',
					id_grupo: this.grupoGabineteId,
					id_usuario: user.id,
				};

				const response = await this.$http.post('items/add_grupos', convite);

				if (response.data.data && response.data.data.id) {
					// Adicionar à lista de integrantes como convite pendente
					this.integrantes.push({
						userId: user.id,
						email: user.email || '',
						nome: user.first_name || 'Usuário',
						tipo: 'convite_pendente',
						conviteId: response.data.data.id,
					});

					window.console.log('✅ Convite criado com sucesso. ID:', response.data.data.id);

					// Limpar busca
					this.nomeBusca = '';
					this.usuariosEncontrados = [];
					this.buscou = false;
					this.mostrarFormularioConvite = false;
					this.linkConviteGerado = '';
					this.linkCopiado = false;
					
					this.mostrarSnackbar(`Convite enviado para ${user.first_name || 'usuário'}!`, 'success');
				}
			} catch (error) {
				window.console.error('Erro ao criar convite:', error);
				this.mostrarSnackbar('Erro ao enviar convite. Tente novamente.', 'error');
			}
		},
		async gerarLinkConvite() {
			if (this.integrantes.length >= 9) return;

			this.gerandoLink = true;
			this.linkCopiado = false;
			try {
				// Garantir que o grupo do gabinete existe
				if (!this.grupoGabineteId) {
					// Buscar ou criar grupo do gabinete
					const { data } = await this.$http.get(
						`items/grupos?filter[user_created][_eq]=${this.$store.state.user.id}&filter[restrito][_eq]=true&filter[nome_grupo][_contains]=Gabinete&fields=id`
					);
					
					if (data.data && data.data.length > 0) {
						this.grupoGabineteId = data.data[0].id;
					} else {
						// Criar grupo do gabinete
						const { data: newGroupData } = await this.$http.post('items/grupos', {
							nome_grupo: `Gabinete de ${this.$store.state.user.first_name}`,
							restrito: true,
							tipo_grupo: 'gabinete',
							status: 'published',
							user_created: this.$store.state.user.id,
							administradores: [{
								directus_users_id: this.$store.state.user.id
							}],
							integrantes: [{
								directus_users_id: this.$store.state.user.id
							}]
						});
						this.grupoGabineteId = newGroupData.data.id;
					}
				}
				
				// Gerar código único para o cupom
				const codigoCupom = `GABINETE_${this.$store.state.user.id}_${Date.now()}`;
				
				// Criar cupom no Directus com o ID do grupo
				const expireDate = new Date();
				expireDate.setMonth(expireDate.getMonth() + 3); // Válido por 3 meses

				await this.$http.post('items/cupom', {
					parceiro_cupom: codigoCupom,
					admin_gabinete_id: this.$store.state.user.id,
					grupo_gabinete_id: this.grupoGabineteId, // Adicionar ID do grupo
					desconto_cupom: 100,
					tipo: 'percentage',
					ciclos: 999, // Ilimitado
					expire_date: expireDate.toISOString(),
					status: 'published',
				});

				// Gerar link completo com o ID do grupo
				const baseUrl = window.location.origin;
				this.linkConviteGerado = `${baseUrl}/cadastro/plano-legislativo?cupom=${codigoCupom}&grupo=${this.grupoGabineteId}`;

				// Adicionar à lista de integrantes (será adicionado ao grupo quando o usuário se cadastrar)
				this.integrantes.push({
					userId: null,
					email: 'Link gerado',
					nome: 'Convite pendente',
					tipo: 'convite',
					codigoCupom: codigoCupom,
				});

				// Copiar automaticamente o link para a área de transferência (sem mostrar snackbar duplicado)
				await this.copiarLink(false);

				this.mostrarSnackbar('Link gerado e copiado com sucesso!', 'success');
			} catch (error) {
				window.console.error('Erro ao gerar link:', error);
				this.mostrarSnackbar('Erro ao gerar link. Tente novamente.', 'error');
			} finally {
				this.gerandoLink = false;
			}
		},
		async copiarLink(mostrarSnackbar = true) {
			if (!this.linkConviteGerado) return;

			this.copiandoLink = true;
			try {
				// Tentar usar a API moderna do clipboard
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(this.linkConviteGerado);
					this.linkCopiado = true;
					if (mostrarSnackbar) {
						this.mostrarSnackbar('Link copiado para a área de transferência!', 'success');
					}
					
					// Resetar após 3 segundos
					setTimeout(() => {
						this.linkCopiado = false;
					}, 3000);
				} else {
					// Fallback para navegadores mais antigos
					const textArea = document.createElement('textarea');
					textArea.value = this.linkConviteGerado;
					textArea.style.position = 'fixed';
					textArea.style.left = '-999999px';
					document.body.appendChild(textArea);
					textArea.select();
					document.execCommand('copy');
					document.body.removeChild(textArea);
					
					this.linkCopiado = true;
					if (mostrarSnackbar) {
						this.mostrarSnackbar('Link copiado para a área de transferência!', 'success');
					}
					
					setTimeout(() => {
						this.linkCopiado = false;
					}, 3000);
				}
			} catch (error) {
				window.console.error('Erro ao copiar link:', error);
				if (mostrarSnackbar) {
					this.mostrarSnackbar('Erro ao copiar link. Tente selecionar e copiar manualmente.', 'error');
				}
			} finally {
				this.copiandoLink = false;
			}
		},
		removerIntegrante(index) {
			if (index < 0 || index >= this.integrantes.length) return;
			
			const integranteRemovido = this.integrantes[index];
			window.console.log('🗑️ Removendo integrante:', integranteRemovido);
			
			// Se for um convite pendente, remover o convite da tabela add_grupos imediatamente
			if (integranteRemovido.tipo === 'convite_pendente' && integranteRemovido.conviteId) {
				this.$http.delete(`items/add_grupos/${integranteRemovido.conviteId}`)
					.then(() => {
						window.console.log('✅ Convite removido da tabela add_grupos');
					})
					.catch((err) => {
						window.console.error('❌ Erro ao remover convite:', err);
					});
			}
			
			// Remover da lista local
			this.integrantes.splice(index, 1);
			
			window.console.log('✅ Integrante removido da lista. Total restante:', this.integrantes.length);
			
			// A remoção do grupo no backend será feita quando o usuário clicar em "Salvar"
			this.mostrarSnackbar(
				`${integranteRemovido.nome || integranteRemovido.email || 'Integrante'} removido da lista. Clique em "Salvar" para confirmar.`,
				'info'
			);
		},
		async salvarIntegrantes() {
			this.loading = true;
			
			// Permitir salvar mesmo com lista vazia (para remover todos os integrantes)
			try {
				// Criar ou obter grupo do gabinete
				if (!this.grupoGabineteId) {
					// Criar novo grupo exclusivo do gabinete
					const { data } = await this.$http.post('items/grupos', {
						nome_grupo: `Gabinete de ${this.$store.state.user.first_name}`,
						restrito: true,
						tipo_grupo: 'gabinete',
						status: 'published',
						user_created: this.$store.state.user.id,
						administradores: [{
							directus_users_id: this.$store.state.user.id
						}],
						integrantes: [{
							directus_users_id: this.$store.state.user.id
						}]
					});
					this.grupoGabineteId = data.data.id;
					window.console.log('✅ Grupo do gabinete criado com ID:', this.grupoGabineteId);
				}

				// Buscar integrantes atuais do grupo (incluindo o ID do relacionamento)
				const { data: grupoData } = await this.$http.get(
					`items/grupos/${this.grupoGabineteId}?fields=*,integrantes.id,integrantes.directus_users_id.id,integrantes.directus_users_id.email,integrantes.directus_users_id.first_name`
				);
				const grupo = grupoData.data;
				
				window.console.log('📋 Integrantes atuais no grupo:', grupo.integrantes);
				window.console.log('📋 Integrantes na lista local:', this.integrantes);
				
				// IMPORTANTE: Não adicionar usuários diretamente ao grupo quando são convites pendentes
				// Os convites pendentes já foram criados na tabela add_grupos e serão processados quando o usuário aceitar
				// Apenas processar remoção de convites se necessário
				
				// IDs dos usuários que devem estar no grupo (apenas usuários já aceitos, não convites pendentes)
				// Convites pendentes (tipo 'convite_pendente') não devem ser adicionados diretamente
				const userIdsParaAdicionar = this.integrantes
					.filter(i => i.userId && i.tipo === 'usuario' && i.tipo !== 'convite_pendente')
					.map(i => i.userId);

				// IDs dos integrantes atuais (excluindo o admin)
				const integrantesAtuais = grupo.integrantes
					.filter(i => i.directus_users_id && i.directus_users_id.id !== this.$store.state.user.id)
					.map(i => i.directus_users_id.id);

				// Buscar informações completas dos integrantes para verificar se foram convidados por link
				// Precisamos verificar se algum integrante foi convidado por link (eh_convidado_gabinete: true)
				const integrantesComInfo = await Promise.all(
					grupo.integrantes
						.filter(i => i.directus_users_id && i.directus_users_id.id !== this.$store.state.user.id)
						.map(async (item) => {
							try {
								const { data: userData } = await this.$http.get(
									`users/${item.directus_users_id.id}?fields=id,eh_convidado_gabinete,admin_gabinete_id`
								);
								return {
									...item,
									eh_convidado_gabinete: userData.data.eh_convidado_gabinete || false,
									admin_gabinete_id: userData.data.admin_gabinete_id || null,
								};
							} catch (err) {
								window.console.warn(`Erro ao buscar info do usuário ${item.directus_users_id.id}:`, err);
								return {
									...item,
									eh_convidado_gabinete: false,
									admin_gabinete_id: null,
								};
							}
						})
				);

				// Remover integrantes que não estão mais na lista local
				// IMPORTANTE: NÃO remover usuários que foram convidados por link (eh_convidado_gabinete: true)
				// Comparar os IDs dos usuários: se um integrante está no grupo mas não está na lista local, deve ser removido
				const integrantesParaRemover = integrantesComInfo.filter(function(item) {
					// Não remover o próprio admin
					if (!item.directus_users_id || item.directus_users_id.id === this.$store.state.user.id) {
						return false;
					}
					
					// VERIFICAÇÃO CRÍTICA: NÃO remover usuários que foram convidados por link e já se cadastraram
					// Se o usuário foi convidado por link (eh_convidado_gabinete: true) e o admin_gabinete_id corresponde ao admin atual,
					// ele deve permanecer no grupo mesmo que não esteja na lista local
					// Isso garante que usuários que se cadastraram pelo link não sejam removidos acidentalmente
					if (item.eh_convidado_gabinete === true && item.admin_gabinete_id === this.$store.state.user.id) {
						window.console.log(`✅ PRESERVANDO usuário convidado por link que já se cadastrou: ${item.directus_users_id.first_name || item.directus_users_id.email} (ID: ${item.directus_users_id.id})`);
						window.console.log(`   - eh_convidado_gabinete: ${item.eh_convidado_gabinete}`);
						window.console.log(`   - admin_gabinete_id: ${item.admin_gabinete_id}`);
						window.console.log(`   - admin atual: ${this.$store.state.user.id}`);
						return false; // NÃO REMOVER
					}
					
					// Se o usuário não está na lista de IDs para adicionar, deve ser removido
					// MAS apenas se NÃO for um usuário convidado por link
					const userId = item.directus_users_id.id;
					const estaNaListaLocal = userIdsParaAdicionar.includes(userId);
					
					if (!estaNaListaLocal) {
						window.console.log(`🗑️ Usuário ${userId} não está na lista local, será removido`);
						return true; // REMOVER
					}
					
					return false; // NÃO REMOVER (está na lista local)
				}.bind(this));

				if (integrantesParaRemover.length > 0) {
					window.console.log('🗑️ Removendo integrantes do grupo:', integrantesParaRemover.map(i => i.directus_users_id.id));
					window.console.log('🗑️ IDs dos relacionamentos para remover:', integrantesParaRemover.map(i => i.id));
					
					try {
						// Remover do grupo usando o ID do relacionamento
						// O Directus espera um array de IDs para deletar
						const idsParaDeletar = integrantesParaRemover.map(i => i.id);
						
						window.console.log('🔍 Tentando remover com IDs:', idsParaDeletar);
						
						const response = await this.$http.patch(`items/grupos/${this.grupoGabineteId}`, {
							integrantes: {
								delete: idsParaDeletar
							}
						});
						
						window.console.log('✅ Resposta da remoção:', response.data);
						
						// Verificar se a remoção foi bem-sucedida fazendo uma nova busca
						await new Promise(resolve => setTimeout(resolve, 500));
						const { data: grupoVerificacao } = await this.$http.get(
							`items/grupos/${this.grupoGabineteId}?fields=integrantes.directus_users_id.id`
						);
						
						const integrantesRestantes = grupoVerificacao.data.integrantes || [];
						const idsRestantes = integrantesRestantes
							.filter(i => i.directus_users_id && i.directus_users_id.id !== this.$store.state.user.id)
							.map(i => i.directus_users_id.id);
						
						window.console.log('✅ IDs dos integrantes restantes após remoção:', idsRestantes);
						
						// Verificar se algum dos removidos ainda está presente
						const idsRemovidos = integrantesParaRemover.map(i => i.directus_users_id.id);
						const aindaPresentes = idsRemovidos.filter(id => idsRestantes.includes(id));
						
						if (aindaPresentes.length > 0) {
							window.console.warn('⚠️ Alguns usuários ainda estão no grupo após tentativa de remoção:', aindaPresentes);
							
							// Tentar remoção alternativa: deletar um por um
							for (const item of integrantesParaRemover) {
								if (aindaPresentes.includes(item.directus_users_id.id)) {
									try {
										// Tentar remover individualmente usando PATCH
										await this.$http.patch(`items/grupos/${this.grupoGabineteId}`, {
											integrantes: {
												delete: [item.id]
											}
										});
										window.console.log(`✅ Relacionamento ${item.id} removido individualmente`);
										// Aguardar um pouco entre cada remoção
										await new Promise(resolve => setTimeout(resolve, 300));
									} catch (err) {
										window.console.error(`❌ Erro ao remover relacionamento ${item.id}:`, err);
										if (err.response) {
											window.console.error('Detalhes:', err.response.data);
										}
									}
								}
							}
							
							// Verificar novamente após remoção individual
							await new Promise(resolve => setTimeout(resolve, 500));
							const { data: grupoVerificacaoFinal } = await this.$http.get(
								`items/grupos/${this.grupoGabineteId}?fields=integrantes.directus_users_id.id`
							);
							
							const integrantesRestantesFinal = grupoVerificacaoFinal.data.integrantes || [];
							const idsRestantesFinal = integrantesRestantesFinal
								.filter(i => i.directus_users_id && i.directus_users_id.id !== this.$store.state.user.id)
								.map(i => i.directus_users_id.id);
							
							const aindaPresentesFinal = idsRemovidos.filter(id => idsRestantesFinal.includes(id));
							
							if (aindaPresentesFinal.length > 0) {
								window.console.error('❌ Ainda há usuários não removidos após todas as tentativas:', aindaPresentesFinal);
								throw new Error(`Não foi possível remover alguns integrantes: ${aindaPresentesFinal.join(', ')}`);
							} else {
								window.console.log('✅ Todos os integrantes foram removidos com sucesso após remoção individual');
							}
						} else {
							window.console.log('✅ Todos os integrantes foram removidos com sucesso');
						}
					} catch (error) {
						window.console.error('❌ Erro ao remover integrantes:', error);
						if (error.response) {
							window.console.error('Detalhes do erro:', error.response.data);
						}
						throw error;
					}
				}

				// Processar remoção de convites pendentes que foram removidos da lista
				// Buscar todos os convites pendentes deste grupo
				const { data: convitesPendentesData } = await this.$http.get(
					`items/add_grupos?filter[id_grupo][_eq]=${this.grupoGabineteId}&filter[status][_eq]=pendente&fields=id,id_usuario`
				);
				
				const convitesPendentesAtuais = convitesPendentesData.data || [];
				const convitesIdsNaLista = this.integrantes
					.filter(i => i.tipo === 'convite_pendente' && i.conviteId)
					.map(i => i.conviteId);
				
				// Remover convites que não estão mais na lista
				const convitesParaRemover = convitesPendentesAtuais.filter(convite => 
					!convitesIdsNaLista.includes(convite.id)
				);
				
				for (const convite of convitesParaRemover) {
					try {
						await this.$http.delete(`items/add_grupos/${convite.id}`);
						window.console.log(`✅ Convite removido: ${convite.id}`);
					} catch (err) {
						window.console.error(`❌ Erro ao remover convite ${convite.id}:`, err);
					}
				}

				// IMPORTANTE: Não adicionar usuários diretamente ao grupo quando são convites pendentes
				// Os convites pendentes já foram criados na tabela add_grupos e serão processados quando o usuário aceitar
				// Apenas adicionar usuários que já foram aceitos (não são convites pendentes)
				const integrantesParaAdicionar = userIdsParaAdicionar.filter(function(userId) {
					return !integrantesAtuais.includes(userId);
				});

				if (integrantesParaAdicionar.length > 0) {
					// Adicionar usuários ao grupo como integrantes (sem permissões de admin)
					// Isso é apenas para usuários que já foram aceitos anteriormente
					await this.$http.patch(`items/grupos/${this.grupoGabineteId}`, {
						integrantes: {
							create: integrantesParaAdicionar.map(function(userId) {
								return {
									directus_users_id: { id: userId },
									grupos_id: this.grupoGabineteId
								};
							}.bind(this))
						}
					});

					// IMPORTANTE: Para cada usuário adicionado, atribuir plano legislativo e marcar como convidado
					// Isso garante que usuários existentes recebam as mesmas condições dos convidados por link
					for (const userId of integrantesParaAdicionar) {
						try {
							await this.$http.patch(`users/${userId}`, {
								plano: 'plano-legislativo',
								eh_convidado_gabinete: true,
								admin_gabinete_id: this.$store.state.user.id,
							});
							window.console.log(`✅ Plano legislativo atribuído ao usuário ${userId}`);
						} catch (err) {
							window.console.error(`❌ Erro ao atribuir plano ao usuário ${userId}:`, err);
						}
					}
				}

				// Marcar que o gabinete foi configurado
				await this.$http.patch(`users/${this.$store.state.user.id}`, {
					gabinete_configurado: true,
				});

				// Aguardar um pouco para garantir que todas as operações foram persistidas
				await new Promise(resolve => setTimeout(resolve, 500));

				// Recarregar integrantes para garantir que a lista está atualizada
				await this.carregarIntegrantesExistentes();

				// Atualizar informações do usuário no store
				await this.$store.dispatch('getInfo');

				this.mostrarSnackbar('Gabinete configurado com sucesso!', 'success');

				// Fechar a dialog após salvar com sucesso
				this.loading = false;
				this.dialog = false;
				this.$emit('input', false);
			} catch (error) {
				window.console.error('Erro ao salvar integrantes:', error);
				this.mostrarSnackbar('Erro ao salvar integrantes. Tente novamente.', 'error');
			} finally {
				this.loading = false;
			}
		},
		closeDialog() {
			if (this.loading) return;
			
			// IMPORTANTE: Ao fechar a dialog sem salvar, não fazer nenhuma alteração no grupo
			// Apenas fechar a dialog. As alterações só serão aplicadas quando o usuário clicar em "Salvar"
			window.console.log('🔒 Fechando dialog sem salvar - nenhuma alteração será aplicada ao grupo');
			
			this.dialog = false;
			this.$emit('input', false);
		},
		mostrarSnackbar(text, color = 'success') {
			this.snackbarText = text;
			this.snackbarColor = color;
			this.snackbar = true;
		},
	},
};
</script>

<style scoped>
.v-chip {
	cursor: default;
}

.chip-with-hover:hover {
	border: 1px solid #F15A24;
	border-radius: 8px;
	background-color: #fff5f2;
}
</style>

